/**
 * Repartition d'une depense entre plusieurs tiers, et suivi des creances.
 *
 * ## La cascade
 *
 * Les parts s'appliquent par `ordre` croissant sur une assiette qui fond au fur
 * et a mesure :
 *
 *   Dentiste CHF 400.00
 *     ordre 1  Caisse maladie   montant 150.00          reste 250.00
 *     ordre 2  Complementaire   montant  50.00          reste 200.00
 *     ordre 3  Ex-epouse        50 % du reste → 100.00  reste 100.00
 *     ordre 4  Moi              solde        → 100.00  reste   0.00
 *
 * Des parts de **meme ordre** forment un palier : elles se calculent toutes sur
 * le reste tel qu'il etait a l'entree du palier. C'est ce qui permet d'ecrire
 * « 50 % du reste pour elle, 50 % du reste pour moi » sans que la seconde
 * moitie se calcule sur ce que la premiere a laisse.
 *
 * ## Qui absorbe l'arrondi
 *
 * Ce qui subsiste apres la derniere part revient **implicitement a moi**. Une
 * depense sans aucune part est donc a 100 % a ma charge — le cas courant ne
 * coute aucune saisie — et une part `solde` n'est qu'une facon explicite de
 * designer qui recoit ce reliquat.
 *
 * La somme des parts egale ainsi **toujours** le total, au centime : c'est
 * l'invariant sur lequel repose la credibilite d'un justificatif.
 */

import { pourcentageDe, somme } from '../domain/argent'
import type { Cents, Id, Part, PartModele, Reglement } from '../domain/types'

/** Ce dont le calcul a besoin : une part, rattachee ou non a une operation. */
export type PartCalculable = Pick<Part, 'tiersId' | 'ordre' | 'base' | 'mode' | 'valeur'> & {
  id?: Id
  libelle?: string
}

export interface LigneRepartition<P extends PartCalculable = PartCalculable> {
  part: P
  /** Assiette effectivement utilisee : le total, ou le reste a l'entree du palier. */
  assiette: Cents
  montant: Cents
  /** Reste apres le palier auquel appartient cette part. */
  resteApres: Cents
}

export interface Repartition<P extends PartCalculable = PartCalculable> {
  total: Cents
  lignes: LigneRepartition<P>[]
  /** Reliquat non attribue, qui revient implicitement a moi. */
  reliquat: Cents
  /** Tout ce qui est a ma charge : parts explicites a mon nom + reliquat. */
  partMoi: Cents
  /** Tout ce que d'autres doivent supporter. */
  partAutres: Cents
  avertissements: string[]
}

/**
 * Calcule la cascade. Ne leve jamais : une repartition incoherente (parts qui
 * depassent le total) est **bornee** et signalee, parce qu'une saisie en cours
 * de frappe passe forcement par des etats absurdes et que l'ecran doit pouvoir
 * les afficher sans planter.
 */
export function calculerRepartition<P extends PartCalculable>(
  total: Cents,
  parts: readonly P[],
  moiId: Id,
): Repartition<P> {
  const avertissements: string[] = []
  const triees = [...parts].sort((a, b) => a.ordre - b.ordre)

  const soldes = triees.filter((p) => p.mode === 'solde')
  if (soldes.length > 1) {
    avertissements.push('Plusieurs parts « solde » : seule la dernière reçoit le reliquat.')
  }
  const dernierSolde = soldes[soldes.length - 1]

  const lignes: LigneRepartition<P>[] = []
  let reste = total
  let i = 0

  while (i < triees.length) {
    const ordre = triees[i]!.ordre
    const palier: P[] = []
    while (i < triees.length && triees[i]!.ordre === ordre) palier.push(triees[i++]!)

    const resteEntree = reste
    const lignesPalier: LigneRepartition<P>[] = []

    for (const part of palier) {
      // Le solde se traite en dernier, une fois toutes les autres parts posees.
      if (part.mode === 'solde') continue
      const assiette = part.base === 'total' ? total : resteEntree
      let montant =
        part.mode === 'montant' ? Math.round(part.valeur) : pourcentageDe(assiette, part.valeur)

      if (montant < 0) {
        avertissements.push('Une part négative a été ramenée à zéro.')
        montant = 0
      }
      if (montant > reste) {
        avertissements.push(
          'Les parts dépassent le montant de la dépense : la dernière a été réduite à ce qui restait.',
        )
        montant = Math.max(0, reste)
      }
      reste -= montant
      lignesPalier.push({ part, assiette, montant, resteApres: 0 })
    }

    for (const ligne of lignesPalier) ligne.resteApres = reste
    lignes.push(...lignesPalier)

    for (const part of palier) {
      if (part.mode !== 'solde') continue
      // Un solde qui n'est pas le dernier recoit zero : le reliquat appartient
      // au seul solde final, sinon il serait compte deux fois.
      const montant = part === dernierSolde && isDernierPalier(triees, i) ? reste : 0
      if (part === dernierSolde && !isDernierPalier(triees, i)) {
        avertissements.push('La part « solde » doit être la dernière de la cascade.')
      }
      lignes.push({ part, assiette: reste, montant, resteApres: reste - montant })
      reste -= montant
    }
  }

  const reliquat = reste
  const partMoiExplicite = somme(lignes.filter((l) => l.part.tiersId === moiId).map((l) => l.montant))
  const partMoi = partMoiExplicite + reliquat

  // Replacer les lignes dans l'ordre de la cascade (les soldes ont ete poses
  // apres leur palier ; l'ordre d'affichage doit rester celui de la saisie).
  lignes.sort((a, b) => a.part.ordre - b.part.ordre)

  return {
    total,
    lignes,
    reliquat,
    partMoi,
    partAutres: total - partMoi,
    avertissements: [...new Set(avertissements)],
  }
}

function isDernierPalier(triees: readonly PartCalculable[], indexApresPalier: number): boolean {
  return indexApresPalier >= triees.length
}

// ---------------------------------------------------------------------------
// Modeles
// ---------------------------------------------------------------------------

/**
 * Copie les parts d'un modele pour une operation.
 *
 * **Copie**, jamais reference : modifier le modele plus tard ne doit retoucher
 * aucune operation passee, faute de quoi un justificatif deja remis pourrait
 * changer de montant apres coup.
 */
export function partsDepuisModele(
  modele: readonly PartModele[],
  operationId: Id,
  nouvelId: () => Id,
): Part[] {
  return modele.map((p) => ({ ...p, id: nouvelId(), operationId }))
}

/** Operation inverse : figer la cascade d'une operation en modele. */
export function modeleDepuisParts(parts: readonly Part[]): PartModele[] {
  return [...parts]
    .sort((a, b) => a.ordre - b.ordre)
    .map(({ tiersId, ordre, base, mode, valeur, libelle }) => ({
      tiersId,
      ordre,
      base,
      mode,
      valeur,
      libelle,
    }))
}

/** Decrit une regle en clair : « 50 % du reste », « montant fixe », « solde ». */
export function decrireRegle(p: Pick<Part, 'mode' | 'base' | 'valeur'>): string {
  switch (p.mode) {
    case 'montant':
      return 'montant fixe'
    case 'solde':
      return 'solde'
    case 'pourcentage': {
      const pct = new Intl.NumberFormat('de-CH', { maximumFractionDigits: 2 }).format(p.valeur)
      return p.base === 'reste' ? `${pct} % du reste` : `${pct} % du total`
    }
  }
}

// ---------------------------------------------------------------------------
// Creances
// ---------------------------------------------------------------------------

export type StatutCreance = 'attendue' | 'partielle' | 'soldee'

export interface Creance {
  part: Part
  du: Cents
  recu: Cents
  restant: Cents
  statut: StatutCreance
  reglements: Reglement[]
}

/**
 * Etat d'une part a recevoir. Le statut est **derive**, jamais stocke : un
 * statut en base se desynchroniserait au premier reglement supprime.
 *
 * Un trop-percu donne un `restant` negatif, volontairement conserve : c'est
 * une information (a rembourser, ou a imputer sur la facture suivante), pas
 * une erreur a masquer en bornant a zero.
 */
export function etatCreance(part: Part, du: Cents, reglements: readonly Reglement[]): Creance {
  const propres = reglements
    .filter((r) => r.partId === part.id)
    .sort((a, b) => a.date.localeCompare(b.date))
  const recu = somme(propres.map((r) => r.montant))
  const restant = du - recu
  const statut: StatutCreance = du <= 0 || restant <= 0 ? 'soldee' : recu > 0 ? 'partielle' : 'attendue'
  return { part, du, recu, restant, statut, reglements: propres }
}

/** Montant reçu a une date donnee (incluse). Sert au reste a charge effectif
 *  « au 16.09 », qui ne doit pas compter un versement posterieur. */
export function recuAu(creance: Creance, auJour: string): Cents {
  return somme(creance.reglements.filter((r) => r.date <= auJour).map((r) => r.montant))
}
